/**
 * Ryan O'Dowd
 * 2022-09-05
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Linking,
  View,
} from 'react-native';
// @TODO: import FastImage from '@d11/react-native-fast-image'; // @TODO: need this dep? abstract away behind photo?
import Icon from '@react-native-vector-icons/material-design-icons';
import IconButton from '@components/common/IconButton';
import Popover from '@components/common/Popover';
import React from 'react';
import Text from '@components/common/Text';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

const DATE_FORMAT = 'MMMM d, yyyy';
const DATE_NO_YEAR_FORMAT = 'MMMM d';

const Campaign = (props: CampaignProps) => { // @TODO: rename to something like NewEditGoal?
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  return (
    <View style={styles.container}>
      <View style={styles.containerInner}>
        <Text style={styles.organizationName}>{props.organization.name}</Text>
        <Text style={styles.campaignName}>{props.name}</Text>
        <View style={styles.datesWrapper}>
          <Text style={styles.date}>{i18n('runsFrom', dateFnsFormat(new Date(props.start_datetime), DATE_NO_YEAR_FORMAT, {locale: i18n.dateFnsCode}), dateFnsFormat(new Date(props.end_datetime), DATE_FORMAT, {locale: i18n.dateFnsCode}))}</Text>
          {/* @TODO: <Text style={styles.date}>Joined {dateFnsFormat(new Date(props.date_joined), DATE_NO_YEAR_FORMAT, {locale: i18n.dateFnsCode})}</Text> */}
        </View>
        <Text style={styles.campaignDescription}>{props.description}</Text>{/* @TODO: add "read more..." button for long descriptions */}
      </View>
      <View style={styles.sectionWrapper}>
        <View style={styles.lineOne}>
          <Text style={styles.headerText}>{i18n('myProgress')}</Text>
          <Popover.Wrapper
            initiallyOpen={!props.num_wrinkles_earned && !props.num_wrinkles_in_progress}// @TODO: can technically open multiple popovers if user has multiple active campaigns and no wrinkles completed or in progress
            actions={[
              {
                text: i18n('okay'),
              },
            ]}
            popover={
              <Popover.Inner>
                <Popover.Text title={true}>{i18n('supporting', props.organization.name)}</Popover.Text>
                <Popover.Text>{i18n('supportingDescription')}</Popover.Text>
                <View style={styles.iconParagraphWrapper}>
                  <IconButton// @TODO: abstract with passage button
                    style={styles.passageActionButton}// @TODO: include in this component
                    icon='test-tube'// @TODO: update when icon is updated in passage component
                    iconColor={styles.colors.accentLight}
                    // @TODO: this is changing the icon color: disabled={true}
                    size={22}
                    onPress={() => {}}
                  />{/* @TODO: also include version of button that shows levels */}
                  <Popover.Text style={styles.organizationDescriptionPopover}>{i18n('toFulfillPledges')}</Popover.Text>
                </View>
                <Popover.Text>{i18n('pleaseNote', dateFnsFormat(new Date(props.end_datetime), DATE_NO_YEAR_FORMAT, {locale: i18n.dateFnsCode}))}</Popover.Text>
              </Popover.Inner>
            }
          >
            <Icon style={styles.icon} name='information' />
          </Popover.Wrapper>
        </View>
        <View style={styles.myContributions}>
          <View style={styles.contributionRow}>
            <Text style={styles.contributionText}>{i18n('wrinklesCompleted')}:</Text>
            <View style={styles.numWrinklesWrapper}>
              <Text style={styles.numWrinklesText}>{props.num_wrinkles_earned}</Text>
            </View>
          </View>
          <View style={styles.contributionRow}>
            <View style={[styles.numWrinklesWrapper, styles.inProgressNumWrapper]}>
              <Text style={styles.numWrinklesText}>{props.num_wrinkles_in_progress}</Text>
            </View>
            <Text style={styles.contributionText}>{i18n('wrinklesInProgress')}</Text>
          </View>
        </View>
      </View>
      {/* @TODO:
      <Button
        containerStyle={TODO}
        mode='contained'
        icon='web'
        theme={styles.colors.accent}
        onPress={() => Linking.openURL(`${globals.webUrl}/organizations/${props.organization.slug}`)}
      >
        Check progress
      </Button>
      */}
      <View style={styles.actions}>
        <Popover.Wrapper
          actions={[
            {
              text: i18n('moreInformation'),
              otherProps: {
                icon: 'web',
              },
              onPress: () => Linking.openURL(`${globals.webUrl}/organizations/${props.organization.slug}`),
            },
            /* @TODO:
            {
              text: i18n('organizationWebsite'),
              otherProps: {
                icon: 'web',
              },
              onPress: () => Linking.openURL(props.organization.website_url),
            },
            !!props.url && {
              text: i18n('campaignWebsite'),
              otherProps: {
                icon: 'web',
              },
              onPress: () => Linking.openURL(props.url),
            }
            */
          ].filter((action) => action)}
          popover={
            <Popover.Inner>
              <View style={styles.imageWrapper}>
                {/* @TODO:
                <FastImage
                  style={styles.organizationLogo}
                  resizeMode={FastImage.resizeMode.contain}
                  source={{
                    uri: `${globals.webUrl}${props.organization.logo_url}`,
                    priority: FastImage.priority.normal,
                    cache: FastImage.cacheControl.web,
                  }}
                  onError={(e) => {
                    console.error('Failed to load image', e); /* eslint-disable-line no-console / // @TODO: handle this? at the least, crashlytics
                  }}
                />
                */}
              </View>
              <Popover.Text title={true}>{props.organization.name}</Popover.Text>
              <Popover.Text>{props.organization.description}</Popover.Text>
            </Popover.Inner>
          }
        >
          <Icon style={styles.icon} name='information' />
        </Popover.Wrapper>
      </View>
    </View>
  );
};

class CampaignProps {
  description: string;
  end_datetime: string;
  date_joined: string;
  name: string;
  num_wrinkles_earned: number;
  num_wrinkles_in_progress: number;
  organization: {
    description: string,
    logo_url: string,
    name: string,
    slug: string,
    website_url: string,
  }; // @TODO: organization type
  slug: string;
  start_datetime: string;

  url: string;
};

export default Campaign;
